import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { OptionsValuePair } from './OptionsValuePair';
import { formatFeesDisplay } from './optionsOpenPositionsUtils';
import { formatCompactPnl, formatDate, formatDateTime, getPnlClassName } from './optionsHistoryUtils';
import {
  formatCampaignPeriodLabel,
  getCampaignEquationSummary,
  getRollTransition,
  sortCampaignOperations,
} from './optionsCampaignHistoryUtils';
import { OptionsCampaignManagementSection } from './OptionsCampaignManagementSection';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsUnderlyingStrategyTitle } from './OptionsUnderlyingStrategyTitle';
import { formatHistoryRollLabel, formatHistoryStatus } from './optionsHistoryUtils';

export const OptionsCampaignHistorySection = ({ detail, selectedCurrency, currentOperationId = null, testIdPrefix = 'options-campaign-history' }) => {
  const { t, isPortuguese } = useOptionsLocale();
  const orderedOperations = useMemo(() => sortCampaignOperations(detail), [detail]);
  const equationSummary = useMemo(() => getCampaignEquationSummary(detail), [detail]);

  if (!detail?.campaign || !orderedOperations.length) return null;

  return (
    <div className="space-y-5" data-testid={`${testIdPrefix}-section`}>
      <div className="space-y-2" data-testid={`${testIdPrefix}-header`}>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45" data-testid={`${testIdPrefix}-title`}>
          {t('campaignExplainability.historyTitle')}
        </p>
        <p className="text-sm text-white/55" data-testid={`${testIdPrefix}-subtitle`}>
          {t('campaignExplainability.historySubtitle')}
        </p>
      </div>

      <div className="space-y-4" data-testid={`${testIdPrefix}-timeline`}>
        {orderedOperations.map((operation, operationIndex) => {
          const nextOperation = orderedOperations[operationIndex + 1] || null;
          const transition = nextOperation ? getRollTransition(detail, operation.id, nextOperation.id, isPortuguese ? 'pt' : 'en') : null;
          const pnl = formatCompactPnl({
            selectedCurrency,
            nativeValue: operation.net_pnl_base,
            usdValue: operation.net_pnl_usd,
            currency: operation.underlying,
          });
          const fees = formatFeesDisplay({
            selectedCurrency,
            nativeValue: operation.total_fees_base,
            usdValue: operation.total_fees_usd,
            underlying: operation.underlying,
          });
          const pnlLabel = transition
            ? t('campaignExplainability.realizedOnRoll')
            : operation.status === 'OPEN'
              ? t('campaignExplainability.currentOperationPnl')
              : t('campaignExplainability.finalOperationPnl');
          const dateLabel = operation.closed_at ? formatDateTime(operation.closed_at, isPortuguese ? 'pt' : 'en') : formatDateTime(operation.opened_at, isPortuguese ? 'pt' : 'en');
          const dateKeyLabel = operation.closed_at ? t('campaignExplainability.closedAt') : t('campaignExplainability.openedAt');
          const isCurrentOperation = currentOperationId ? operation.id === currentOperationId : operation.status === 'OPEN';

          return (
            <div key={operation.id} className="space-y-3" data-testid={`${testIdPrefix}-operation-${operationIndex}`}>
              <div className="rounded-[28px] border border-white/10 bg-white/[0.03] px-4 py-4 sm:px-5 sm:py-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-badges-${operationIndex}`}>
                      <Badge className="border-white/10 bg-white/[0.05] text-white" data-testid={`${testIdPrefix}-status-${operationIndex}`}>
                        {formatHistoryStatus(operation.status, t)}
                      </Badge>
                      {transition ? (
                        <Badge className="border-[#F0B90B]/20 bg-[#F0B90B]/10 text-[#F7D47A]" data-testid={`${testIdPrefix}-rolled-${operationIndex}`}>
                          {formatHistoryRollLabel(t)}
                        </Badge>
                      ) : null}
                      {isCurrentOperation ? (
                        <Badge className="border-[#00E676]/20 bg-[#00E676]/10 text-[#9BFFBE]" data-testid={`${testIdPrefix}-current-${operationIndex}`}>
                          {t('history.current')}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <OptionsUnderlyingStrategyTitle
                        as="p"
                        underlying={operation.underlying}
                        strategy={operation.strategy_label}
                        prefix={`${formatCampaignPeriodLabel(operation.expiration, isPortuguese ? 'pt' : 'en')} —`}
                        iconSize={15}
                        className="text-base font-semibold text-white"
                        testId={`${testIdPrefix}-heading-${operationIndex}`}
                      />
                      <p className="text-sm text-white/55" data-testid={`${testIdPrefix}-meta-${operationIndex}`}>
                        {t('history.expiration')} · {formatDate(operation.expiration, isPortuguese ? 'pt' : 'en')}
                        <span className="mx-2">•</span>
                        {dateKeyLabel} · {dateLabel}
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:min-w-[280px] sm:grid-cols-2" data-testid={`${testIdPrefix}-metrics-${operationIndex}`}>
                    <ExplainabilityMetric
                      label={pnlLabel}
                      primary={pnl.primary}
                      secondary={pnl.secondary}
                      valueClassName={getPnlClassName(pnl.sign)}
                      testId={`${testIdPrefix}-pnl-${operationIndex}`}
                    />
                    <ExplainabilityMetric
                      label={t('dashboard.fees')}
                      primary={fees.primary}
                      secondary={fees.secondary}
                      valueClassName="text-white"
                      secondaryClassName="text-white/55"
                      testId={`${testIdPrefix}-fees-${operationIndex}`}
                    />
                  </div>
                </div>
              </div>

              {transition ? (
                <div
                  className="ml-2 rounded-2xl border border-[#F0B90B]/20 bg-[#F0B90B]/10 px-4 py-4 sm:ml-6"
                  data-testid={`${testIdPrefix}-roll-${operationIndex}`}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#F7D47A]" data-testid={`${testIdPrefix}-roll-label-${operationIndex}`}>
                    ↓ {formatHistoryRollLabel(t)}
                  </p>
                  <p className="mt-2 text-sm text-[#FFE29A]" data-testid={`${testIdPrefix}-roll-date-${operationIndex}`}>
                    {transition.createdAtLabel}
                  </p>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="rounded-[28px] border border-white/10 bg-white/[0.03] px-4 py-4 sm:px-5 sm:py-5" data-testid={`${testIdPrefix}-campaign-pnl`}>
        <ExplainabilityMetric
          label={t('campaignExplainability.campaignPnl')}
          primary={formatCompactPnl({
            selectedCurrency,
            nativeValue: detail.campaign.campaign_net_pnl_base,
            usdValue: detail.campaign.campaign_net_pnl_usd,
            currency: detail.campaign.underlying,
          }).primary}
          secondary={formatCompactPnl({
            selectedCurrency,
            nativeValue: detail.campaign.campaign_net_pnl_base,
            usdValue: detail.campaign.campaign_net_pnl_usd,
            currency: detail.campaign.underlying,
          }).secondary}
          valueClassName={getPnlClassName(detail.campaign.campaign_net_pnl_usd ?? detail.campaign.campaign_net_pnl_base)}
          testId={`${testIdPrefix}-campaign-pnl-metric`}
        />
      </div>

      {equationSummary ? (
        <div className="rounded-[28px] border border-white/10 bg-black/20 px-4 py-4 sm:px-5 sm:py-5" data-testid={`${testIdPrefix}-summary`}>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45" data-testid={`${testIdPrefix}-summary-title`}>
            {t('campaignExplainability.summaryTitle')}
          </p>
          <p className="mt-2 text-sm text-white/55" data-testid={`${testIdPrefix}-summary-equation`}>
            {t('campaignExplainability.summaryEquation')}
          </p>

          <div className="mt-4 grid gap-3 lg:grid-cols-[1fr,auto,1fr,auto,1fr] lg:items-center" data-testid={`${testIdPrefix}-summary-grid`}>
            <ExplainabilityMetric
              label={t('campaignExplainability.previousRealized')}
              primary={formatCompactPnl({
                selectedCurrency,
                nativeValue: equationSummary.previousOperation.net_pnl_base,
                usdValue: equationSummary.previousOperation.net_pnl_usd,
                currency: equationSummary.previousOperation.underlying,
              }).primary}
              secondary={formatCompactPnl({
                selectedCurrency,
                nativeValue: equationSummary.previousOperation.net_pnl_base,
                usdValue: equationSummary.previousOperation.net_pnl_usd,
                currency: equationSummary.previousOperation.underlying,
              }).secondary}
              valueClassName={getPnlClassName(equationSummary.previousOperation.net_pnl_usd ?? equationSummary.previousOperation.net_pnl_base)}
              testId={`${testIdPrefix}-summary-previous`}
            />
            <div className="hidden text-center text-2xl font-black text-white/25 lg:block" data-testid={`${testIdPrefix}-summary-plus`}>
              +
            </div>
            <ExplainabilityMetric
              label={t('campaignExplainability.currentOperation')}
              primary={formatCompactPnl({
                selectedCurrency,
                nativeValue: equationSummary.currentOperation.net_pnl_base,
                usdValue: equationSummary.currentOperation.net_pnl_usd,
                currency: equationSummary.currentOperation.underlying,
              }).primary}
              secondary={formatCompactPnl({
                selectedCurrency,
                nativeValue: equationSummary.currentOperation.net_pnl_base,
                usdValue: equationSummary.currentOperation.net_pnl_usd,
                currency: equationSummary.currentOperation.underlying,
              }).secondary}
              valueClassName={getPnlClassName(equationSummary.currentOperation.net_pnl_usd ?? equationSummary.currentOperation.net_pnl_base)}
              testId={`${testIdPrefix}-summary-current`}
            />
            <div className="hidden text-center text-2xl font-black text-white/25 lg:block" data-testid={`${testIdPrefix}-summary-equals`}>
              =
            </div>
            <ExplainabilityMetric
              label={t('campaignExplainability.campaignPnl')}
              primary={formatCompactPnl({
                selectedCurrency,
                nativeValue: equationSummary.campaign.campaign_net_pnl_base,
                usdValue: equationSummary.campaign.campaign_net_pnl_usd,
                currency: equationSummary.campaign.underlying,
              }).primary}
              secondary={formatCompactPnl({
                selectedCurrency,
                nativeValue: equationSummary.campaign.campaign_net_pnl_base,
                usdValue: equationSummary.campaign.campaign_net_pnl_usd,
                currency: equationSummary.campaign.underlying,
              }).secondary}
              valueClassName={getPnlClassName(equationSummary.campaign.campaign_net_pnl_usd ?? equationSummary.campaign.campaign_net_pnl_base)}
              testId={`${testIdPrefix}-summary-campaign`}
            />
          </div>
        </div>
      ) : null}

      <OptionsCampaignManagementSection activities={detail?.management_activities || []} testIdPrefix={`${testIdPrefix}-management`} />
    </div>
  );
};

const ExplainabilityMetric = ({
  label,
  primary,
  secondary = null,
  valueClassName = 'text-white',
  secondaryClassName = 'text-white/55',
  testId,
}) => {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</p>
      <OptionsValuePair
        primary={primary}
        secondary={secondary}
        primaryToneClass={valueClassName}
        secondaryToneClass={secondaryClassName}
        testId={`${testId}-value-stack`}
      />
    </div>
  );
};

export default OptionsCampaignHistorySection;